"use client";

import { useState } from "react";
import QRCode from "react-qr-code";
import { PlatformError } from "@/lib/api/client";
import { messageFor } from "@/lib/api/messages";
import { useWallets as useLinkedWallets } from "@/lib/api/profile";
import { useLinkWallet } from "@/lib/api/session";
import { isWalletPayable, useConfirmDeposit, useDepositDetails, type RouteOption } from "@/lib/api/transfers";
import { toBaseUnits } from "@/lib/format/amount";
import { formatMinimum, hasInvalidAmountCharacters, parseTypedAmount, sanitizeAmountInput } from "@/lib/wallet/amount";
import {
  canDeposit,
  DepositAddressConflictError,
  depositFailureMessage,
  transferAmount,
  validateDeposit,
  walletMaxAmount,
} from "@/lib/wallet/deposit";
import { sendTokenTransfer } from "@/lib/wallet/sign";
import { useWalletTokenBalance } from "@/lib/wallet/use-balance";
import { usePlatformEventsContext } from "@/components/shell/PlatformEvents";
import { Web3WalletCard, useWeb3Wallet } from "./Web3Wallet";
import { WalletBalance } from "./WalletBalance";
import { Icon } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";
import { SectionError } from "@/components/ui/states";
import { Field, Note } from "./fields";
import { GeoblockNotice } from "./GeoblockNotice";

/* The platform watches the address it issued: a transfer that lands there is
   detected and credited on its own, and the transactions socket announces it —
   no hash needs handing over. So this screen is the address, the guardrails,
   and a browser-wallet shortcut that still posts its hash the way the
   whitelabel's does, because a hash the platform already has credits faster
   than one it must find. */

export function DepositPanel({ option }: { option: RouteOption }) {
  const details = useDepositDetails(option.route.id);

  if (details.isPending) {
    return <div className="h-[184px] w-full animate-pulse rounded-lg bg-elevation-2" />;
  }

  if (details.isError) {
    /* A 403 here is the region, not a fault — the whitelabel's Geoblock. */
    if (details.error instanceof PlatformError && details.error.status === 403) {
      return <GeoblockNotice />;
    }

    return (
      <SectionError
        title="No deposit address"
        body={messageFor(
          details.error instanceof PlatformError ? details.error : new PlatformError(0, "network", ""),
          "wallet",
        )}
      />
    );
  }

  return (
    <div className="flex w-full flex-col gap-[12px]">
      <Address option={option} address={details.data.address} minimumAmount={details.data.minimumAmount} />
      <Pay
        option={option}
        recipient={details.data.address}
        minimumAmount={details.data.minimumAmount}
      />
    </div>
  );
}

function Address({
  option,
  address,
  minimumAmount,
}: {
  option: RouteOption;
  address: string;
  minimumAmount: string;
}) {
  const [copied, setCopied] = useState(false);
  const [showQr, setShowQr] = useState(false);
  const { token } = option;
  const minimum = toBaseUnits(minimumAmount);

  const copy = async () => {
    await navigator.clipboard.writeText(address);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="w-full rounded-lg border border-border p-[12px]">
      <p className="mb-[8px] text-[12px] font-bold leading-[1.6] text-fg-subtle">
        Your {token.symbol} deposit address
      </p>

      <div className="flex gap-[8px]">
        <button
          type="button"
          onClick={copy}
          className="flex h-[42px] min-w-0 flex-1 items-center justify-between gap-[8px] rounded-lg border border-border-input bg-elevation-2 py-[8px] pl-[12px] pr-[6px] text-left text-[12px] font-bold leading-[1.6] text-fg-subtle transition-colors hover:bg-elevation-3"
        >
          <span className="truncate">{address}</span>
          <Icon name={copied ? "check" : "copy"} size={18} className="shrink-0 text-fg" />
        </button>

        {/* The whitelabel's QR button: a phone wallet scans the address
            rather than having it retyped. */}
        <button
          type="button"
          onClick={() => setShowQr((current) => !current)}
          aria-label={showQr ? "Hide QR code" : "Show QR code"}
          aria-expanded={showQr}
          className="grid h-[42px] w-[42px] shrink-0 place-items-center rounded-lg border border-border-input bg-elevation-2 text-fg transition-colors hover:bg-elevation-3"
        >
          <Icon name="qr" size={18} />
        </button>
      </div>

      {showQr && (
        <div className="mt-[8px] flex justify-center">
          {/* White behind the code whatever the theme: scanners want dark
              modules on a light ground. */}
          <div className="rounded-lg bg-white p-[12px]">
            <QRCode value={address} size={128} aria-label={`QR code for ${address}`} />
          </div>
        </div>
      )}

      {/* The network is not a detail — the same address on the wrong chain is
          how a deposit is lost for good. The whitelabel's DepositAddressInfo. */}
      <div className="mt-[8px] flex items-start gap-[8px] text-[9px] leading-[1.5] text-fg-subtle">
        <Icon name="info" size={14} className="mt-[1px] shrink-0 text-info-fg" />
        <p>
          Send only <strong className="text-fg">{token.symbol}</strong> on{" "}
          <strong className="text-fg">{token.network.name}</strong> to this wallet.
          {minimum > 0n && (
            <>
              {" "}
              <span className="underline">
                Minimum deposit amount{" "}
                <strong className="text-fg">
                  {formatMinimum(minimum, token)} {token.symbol}
                </strong>
                .
              </span>
            </>
          )}
        </p>
      </div>
    </div>
  );
}

/* The whole flow in one button, for a player whose wallet can reach the token's
   network: transfer on chain, then hand the hash straight to the platform. */
function Pay({
  option,
  recipient,
  minimumAmount,
}: {
  option: RouteOption;
  recipient: string;
  minimumAmount: string;
}) {
  const { connection } = useWeb3Wallet();
  const linked = useLinkedWallets(true);
  const link = useLinkWallet();
  const confirm = useConfirmDeposit();
  const { showPendingTransaction } = usePlatformEventsContext();
  const [amount, setAmount] = useState("");
  const [pending, setPending] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const { token } = option;
  const payable = isWalletPayable(option);
  /* What the connected wallet holds of this token on this route's chain — the
     figure the whitelabel shows under the address, and the ceiling on the
     amount. */
  const balance = useWalletTokenBalance(connection, token, payable && connection !== null);

  if (!payable) {
    return null;
  }

  const parsed = parseTypedAmount(amount, token.decimals);
  /* A route with no minimum still takes at least one whole token from a
     wallet payment (lib/wallet/deposit.ts effectiveMinimum). */
  const limits = { minimumAmount, token, balance: balance.data ?? null };
  const errors = validateDeposit({ amount }, limits);
  const ready = canDeposit({ amount }, errors);

  const pay = async () => {
    if (parsed === null || !ready) {
      return;
    }

    setPending(true);
    setFailure(null);

    try {
      if (!connection) return;
      const known = linked.data ?? [];

      if (!known.some((entry) => entry.address.toLowerCase() === connection.address.toLowerCase())) {
        /* The account does not own this address yet, so it is added by
           signature first. A 409 here means another account (or a
           withdrawal destination) already holds it — the whitelabel refuses
           the deposit with its conflict message rather than send tokens the
           platform will not credit to this account. */
        if ((await link.mutateAsync(connection.wallet)) === null) {
          throw new DepositAddressConflictError();
        }
      }

      /* The platform's own client truncates the transfer to a whole multiple
         of 100 base units before sending. Matched here rather than reasoned
         about: the remainder stays in the player's wallet either way, and a
         deposit the platform will not account for is the worse outcome. */
      const hash = await sendTokenTransfer(connection.wallet, {
        from: connection.address,
        token: token.address,
        recipient,
        amount: transferAmount(parsed),
        chainId: token.network.attributes.chainId as number,
      });

      await confirm.mutateAsync({
        routeId: option.route.id,
        hash,
        sourceWalletAddress: connection.address,
      });

      /* Until the transfer's own event settles it (PlatformEvents). */
      showPendingTransaction("Deposit pending...");
      setAmount("");
    } catch (error) {
      setFailure(depositFailureMessage(error));
    } finally {
      setPending(false);
    }
  };

  return (
    <form
      className="flex w-full flex-col gap-[4px] rounded-lg border border-border p-[12px]"
      onSubmit={(event) => {
        event.preventDefault();
        void pay();
      }}
    >
      <p className="mb-[4px] text-[12px] font-bold leading-[1.6] text-fg">Web3 Wallet</p>

      <Web3WalletCard chain="evm" />

      {connection && (
        <>
          <WalletBalance balance={balance} token={token} />
      <Field
        id="deposit-amount"
        label={`Amount (${token.symbol})`}
        value={amount}
        onChange={(value) => setAmount(hasInvalidAmountCharacters(value) ? sanitizeAmountInput(value) : value)}
        inputMode="decimal"
        placeholder="0.00"
        numeric
        error={errors.amount}
        action={
          <button
            type="button"
            onClick={() => {
              if (balance.data) setAmount(walletMaxAmount(balance.data, token));
            }}
            disabled={!balance.data}
            className="h-[30px] rounded-md bg-elevation-3 px-[10px] text-[12px] font-bold leading-[1.6] text-fg transition-colors hover:bg-elevation-4 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Max
          </button>
        }
      />

      <Button
        type="submit"
        size="lg"
        className="mt-[8px] w-full"
        disabled={pending || linked.isPending || !ready}
      >
        {pending ? "Confirm in your wallet…" : `Deposit with ${connection.wallet.name}`}
      </Button>

      {confirm.isSuccess && !pending && (
        <Note tone="success">Deposit submitted. It lands once the network confirms it.</Note>
      )}

      {failure && <Note tone="error">{failure}</Note>}
        </>
      )}
    </form>
  );
}
